import React, { useEffect, useRef, useState } from "react";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import { trackCampaignEvent } from "../utils/campaignMeasurement";
import { Trans, useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api from "../utils/api";
import { storeAuthSession } from "../utils/auth";
import LandingPhoneMockup from "../components/landing/LandingPhoneMockup";
import {
  rememberLandingLanguagePreference,
  saveRegionalPreferencesLocally,
} from "../utils/regionalPreferences";
import "./AuthLandingPage.css";

import heroMap from "../assets/landing/hero-map-clean.png";
import mapRetailStreet from "../assets/landing/map-retail-street.png";
import deHeroMap from "../assets/landing/de/hero-map-clean.png";
import enHeroMap from "../assets/landing/en/hero-map-clean.png";
import enIphoneInterest from "../assets/landing/en/iphone-interest.png";
import enIphoneInterestSaved from "../assets/landing/en/iphone-interest-saved.png";
import enIpadOwnerInterests from "../assets/landing/en/ipad-owner-interests.png";
import enLaptopBrokerOverview from "../assets/landing/en/laptop-broker-overview.png";
import enStreetView from "../assets/landing/en/street-view.png";
import esHeroMap from "../assets/landing/es/hero-map-clean.png";
import esIphoneInterest from "../assets/landing/es/iphone-interest.png";
import esIphoneInterestSaved from "../assets/landing/es/iphone-interest-saved.png";
import esIpadOwnerInterests from "../assets/landing/es/ipad-owner-interests.png";
import esLaptopBrokerOverview from "../assets/landing/es/laptop-broker-overview.png";
import esIphoneRentalOffer from "../assets/landing/es/iphone-rental-offer.png";
import esStreetView from "../assets/landing/es/street-view.png";
import frHeroMap from "../assets/landing/fr/hero-map-clean.png";
import frIphoneInterest from "../assets/landing/fr/iphone-interest.png";
import frIphoneInterestSaved from "../assets/landing/fr/iphone-interest-saved.png";
import frIpadOwnerInterests from "../assets/landing/fr/ipad-owner-interests.png";
import frLaptopBrokerOverview from "../assets/landing/fr/laptop-broker-overview.png";
import frIphoneRentalOffer from "../assets/landing/fr/iphone-rental-offer.png";
import frStreetView from "../assets/landing/fr/street-view.png";
import itHeroMap from "../assets/landing/it/hero-map-clean.png";
import itIphoneInterest from "../assets/landing/it/iphone-interest.png";
import itIphoneInterestSaved from "../assets/landing/it/iphone-interest-saved.png";
import itIpadOwnerInterests from "../assets/landing/it/ipad-owner-interests.png";
import itLaptopBrokerOverview from "../assets/landing/it/laptop-broker-overview.png";
import itIphoneRentalOffer from "../assets/landing/it/iphone-rental-offer.png";
import itStreetView from "../assets/landing/it/street-view.png";
import iphoneInterest from "../assets/landing/iphone-interest.png";
import iphoneInterestSaved from "../assets/landing/iphone-interest-saved.png";
import ipadOwnerInterests from "../assets/landing/ipad-owner-interests.png";
import laptopBrokerOverview from "../assets/landing/laptop-broker-overview.png";
import iphoneRentalOffer from "../assets/landing/iphone-rental-offer.png";
import streetView from "../assets/landing/street-view.png";

const BLOCKED_PERSONAL_EMAIL_DOMAINS = new Set([
  "gmail.com",
  "googlemail.com",
  "hotmail.com",
  "hotmail.nl",
  "outlook.com",
  "outlook.nl",
  "live.com",
  "live.nl",
  "msn.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "yahoo.com",
  "yahoo.nl",
  "aol.com",
  "proton.me",
  "protonmail.com",
  "pm.me",
  "gmx.com",
  "gmx.net",
  "mail.com",
  "zoho.com",
  "tutanota.com",
  "tuta.com",
]);

const ROLE_KEYS = [
  { value: "retailer", key: "retailer" },
  { value: "owner", key: "owner" },
  { value: "broker", key: "broker" },
];

const LANDING_LANGUAGES = [
  { code: "nl", label: "NL" },
  { code: "en", label: "EN" },
  { code: "de", label: "DE" },
  { code: "fr", label: "FR" },
  { code: "es", label: "ES" },
  { code: "it", label: "IT" },
];

const defaultLandingAssets = {
  heroMap,
  mapRetailStreet,
  iphoneInterest,
  iphoneInterestSaved,
  ipadOwnerInterests,
  laptopBrokerOverview,
  iphoneRentalOffer,
  streetView,
};

const landingAssetsByLanguage = {
  nl: defaultLandingAssets,
  en: {
    ...defaultLandingAssets,
    heroMap: enHeroMap,
    iphoneInterest: enIphoneInterest,
    iphoneInterestSaved: enIphoneInterestSaved,
    ipadOwnerInterests: enIpadOwnerInterests,
    laptopBrokerOverview: enLaptopBrokerOverview,
    streetView: enStreetView,
  },
  de: {
    ...defaultLandingAssets,
    heroMap: deHeroMap,
  },
  fr: {
    ...defaultLandingAssets,
    heroMap: frHeroMap,
    iphoneInterest: frIphoneInterest,
    iphoneInterestSaved: frIphoneInterestSaved,
    ipadOwnerInterests: frIpadOwnerInterests,
    laptopBrokerOverview: frLaptopBrokerOverview,
    iphoneRentalOffer: frIphoneRentalOffer,
    streetView: frStreetView,
  },
  es: {
    ...defaultLandingAssets,
    heroMap: esHeroMap,
    iphoneInterest: esIphoneInterest,
    iphoneInterestSaved: esIphoneInterestSaved,
    ipadOwnerInterests: esIpadOwnerInterests,
    laptopBrokerOverview: esLaptopBrokerOverview,
    iphoneRentalOffer: esIphoneRentalOffer,
    streetView: esStreetView,
  },
  it: {
    ...defaultLandingAssets,
    heroMap: itHeroMap,
    iphoneInterest: itIphoneInterest,
    iphoneInterestSaved: itIphoneInterestSaved,
    ipadOwnerInterests: itIpadOwnerInterests,
    laptopBrokerOverview: itLaptopBrokerOverview,
    iphoneRentalOffer: itIphoneRentalOffer,
    streetView: itStreetView,
  },
};

const getLanguageCode = (language = "en") => String(language).split("-")[0].toLowerCase();

const getLandingAssets = (language) =>
  landingAssetsByLanguage[getLanguageCode(language)] || landingAssetsByLanguage.en || defaultLandingAssets;

const isBusinessEmail = (email = "") => {
  const domain = String(email).trim().toLowerCase().split("@")[1] || "";
  return Boolean(domain) && !BLOCKED_PERSONAL_EMAIL_DOMAINS.has(domain);
};

const panelModeFromPath = (pathname) => {
  if (pathname === "/login") return "login";
  if (pathname === "/register") return "register";
  if (pathname === "/forgot-password") return "forgot";
  return "";
};

const AuthLandingPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const registerSubmittingRef = useRef(false);
  const registrationStartedRef = useRef(false);
  const panelRef = useRef(null);

  const currentLanguage = getLanguageCode(i18n.resolvedLanguage || i18n.language || "en");
  const landingAssets = getLandingAssets(currentLanguage);
  const roleOptions = ROLE_KEYS.map((option) => ({
    value: option.value,
    label: t(`landing.roles.options.${option.key}`),
  }));

  const initialPathMode = panelModeFromPath(location.pathname);
  const [panelOpen, setPanelOpen] = useState(Boolean(initialPathMode));
  const [mode, setMode] = useState(initialPathMode || "login");

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [twoFactorToken, setTwoFactorToken] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginNotice, setLoginNotice] = useState(location.state?.notice || "");

  const [registerForm, setRegisterForm] = useState({
    email: "",
    password: "",
    role: "",
    firstName: "",
    lastName: "",
    companyName: "",
  });
  const [registerLegalAccepted, setRegisterLegalAccepted] = useState(false);
  const [registerError, setRegisterError] = useState("");
  const [registerNotice, setRegisterNotice] = useState("");

  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [forgotNotice, setForgotNotice] = useState("");
  const [forgotDevResetUrl, setForgotDevResetUrl] = useState("");

  useEffect(() => {
    const pathMode = panelModeFromPath(location.pathname);
    if (pathMode) {
      setMode(pathMode);
      setPanelOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const emailParam = params.get("email");
    const noticeParam = params.get("notice");

    if (emailParam) {
      setLoginEmail(emailParam);
      setMode("login");
      setPanelOpen(true);
    }

    if (noticeParam === "verified") {
      setLoginNotice(t("landing.auth.notices.emailVerified"));
      setMode("login");
      setPanelOpen(true);
    }
  }, [location.search, t]);

  useEffect(() => {
    if (location.state?.notice) {
      setLoginNotice(location.state.notice);
      setMode("login");
      setPanelOpen(true);
    }
  }, [location.state]);

  useEffect(() => {
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  const resetMessages = () => {
    setLoginError("");
    setRegisterError("");
    setRegisterNotice("");
    setForgotError("");
    setForgotNotice("");
    setForgotDevResetUrl("");
  };

  const openPanel = (nextMode) => {
    resetMessages();
    setMode(nextMode);
    setPanelOpen(true);
    setTwoFactorToken("");
    setTwoFactorCode("");
    navigate(
      nextMode === "login"
        ? "/login"
        : nextMode === "register"
          ? "/register"
          : "/forgot-password",
      { replace: false }
    );
    window.requestAnimationFrame(() => {
      if (!panelRef.current) return;

      try {
        panelRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch {
        panelRef.current.scrollIntoView(true);
      }
    });
  };

  const closePanel = () => {
    setPanelOpen(false);
    resetMessages();
    setTwoFactorToken("");
    setTwoFactorCode("");
    if (panelModeFromPath(location.pathname)) {
      navigate("/", { replace: true });
    }
  };

  const handleRegisterChange = (key, value) => {
    setRegisterForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleLanguageChange = (event) => {
    const nextLanguage = event.target.value;
    void i18n.changeLanguage(nextLanguage);

    let previous = {};
    try {
      const raw = localStorage.getItem("retailmaps:regional-preferences");
      previous = raw ? JSON.parse(raw) : {};
    } catch {
      previous = {};
    }

    saveRegionalPreferencesLocally({
      ...previous,
      language: nextLanguage,
      currency: previous.currency || "auto",
      units: previous.units || "auto",
    });
    rememberLandingLanguagePreference(nextLanguage);
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginError("");
    setLoginNotice("");

    try {
      const response = await api.post("/api/login", {
        email: loginEmail.trim().toLowerCase(),
        password: loginPassword,
      });
      const { success, token, user, requiresTwoFactor, twoFactorToken: nextToken, message } =
        response.data || {};

      if (success && token && user) {
        storeAuthSession(token, user);
        navigate("/map");
        return;
      }

      if (requiresTwoFactor && nextToken) {
        setTwoFactorToken(nextToken);
        setTwoFactorCode("");
        setLoginNotice(message || t("landing.auth.notices.twoFactorRequired"));
        return;
      }

      setLoginError(t("landing.auth.errors.loginFailed"));
    } catch (err) {
      setLoginError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          t("landing.auth.errors.loginFailed")
      );
    }
  };

  const handleTwoFactorLogin = async (event) => {
    event.preventDefault();
    setLoginError("");

    try {
      const response = await api.post("/api/login/2fa", {
        twoFactorToken,
        code: twoFactorCode,
      });
      const { success, token, user } = response.data || {};

      if (success && token && user) {
        storeAuthSession(token, user);
        navigate("/map");
        return;
      }

      setLoginError(t("landing.auth.errors.twoFactorFailed"));
    } catch (err) {
      setLoginError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          t("landing.auth.errors.twoFactorFailed")
      );
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    if (registerSubmittingRef.current) return;
    registerSubmittingRef.current = true;
    setRegisterError("");
    setRegisterNotice("");

    if (!isBusinessEmail(registerForm.email)) {
      setRegisterError(t("landing.auth.errors.businessEmail"));
      registerSubmittingRef.current = false;
      return;
    }

    if (!registerForm.role) {
      setRegisterError(t("landing.auth.errors.roleRequired"));
      registerSubmittingRef.current = false;
      return;
    }

    if (!registerForm.companyName.trim()) {
      setRegisterError(t("landing.auth.errors.companyRequired"));
      registerSubmittingRef.current = false;
      return;
    }

    if (!registerLegalAccepted) {
      setRegisterError(t("landing.auth.errors.legalRequired"));
      registerSubmittingRef.current = false;
      return;
    }

    try {
      const response = await api.post("/api/register", {
        ...registerForm,
        email: registerForm.email.trim().toLowerCase(),
        firstName: registerForm.firstName.trim(),
        lastName: registerForm.lastName.trim(),
        companyName: registerForm.companyName.trim(),
        website: "",
        phone: "",
      });

      const { success, requiresEmailVerification, message } = response.data || {};
      if (response.status === 201 && success && requiresEmailVerification) {
        trackCampaignEvent('sign_up', { role: registerForm.role }, i18n.resolvedLanguage || i18n.language);
        setRegisterNotice(message || t("landing.auth.notices.registerSuccess"));
        localStorage.setItem(
          "retailmaps:legal-consent:v1",
          JSON.stringify({
            privacyAccepted: true,
            termsAccepted: true,
            version: 1,
            acceptedAt: new Date().toISOString(),
          })
        );
        setRegisterForm((prev) => ({ ...prev, password: "" }));
        registerSubmittingRef.current = false;
        return;
      }

      setRegisterError(t("landing.auth.errors.registerFailed"));
    } catch (err) {
      setRegisterError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          t("landing.auth.errors.registerFailed")
      );
    } finally {
      registerSubmittingRef.current = false;
    }
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    setForgotError("");
    setForgotNotice("");
    setForgotDevResetUrl("");

    try {
      const response = await api.post("/api/forgot-password", {
        email: forgotEmail.trim().toLowerCase(),
      });

      setForgotNotice(response.data?.message || t("landing.auth.notices.forgotSent"));
      setForgotDevResetUrl(response.data?.devResetUrl || "");
    } catch (err) {
      setForgotError(err.response?.data?.error || t("landing.auth.errors.forgotFailed"));
    }
  };

  return (
    <div
      className="auth-landing"
      style={{
        "--auth-hero-map": `url(${landingAssets.heroMap})`,
        "--auth-retail-map": `url(${landingAssets.mapRetailStreet})`,
        "--auth-map-wide": `url(${landingAssets.mapRetailStreet})`,
      }}
    >
      <header className="auth-nav">
        <div className="auth-container auth-nav-inner">
          <button type="button" className="auth-brand" onClick={() => navigate("/")}>
            <span className="auth-brand-main">Retailmaps</span>
            <span className="auth-brand-domain">.com</span>
          </button>
          <nav className="auth-nav-links" aria-label={t("landing.nav.aria")}>
            <a href="#voor-wie">{t("landing.nav.forWho")}</a>
            <a href="#hoe-het-werkt">{t("landing.nav.howItWorks")}</a>
            <a href="#product">{t("landing.nav.product")}</a>
            <a href="#prijs">{t("landing.nav.price")}</a>
          </nav>
          <div className="auth-nav-actions">
            <select
              className="auth-language-select"
              value={currentLanguage}
              onChange={handleLanguageChange}
              aria-label={t("landing.language.select")}
            >
              {LANDING_LANGUAGES.map((language) => (
                <option key={language.code} value={language.code}>
                  {language.label}
                </option>
              ))}
            </select>
            <button type="button" className="auth-btn auth-btn-secondary" onClick={() => openPanel("login")}>
              {t("landing.actions.login")}
            </button>
            <button type="button" className="auth-btn auth-btn-primary auth-btn-register" onClick={() => openPanel("register")}>
              {t("landing.actions.startFree")}
            </button>
          </div>
        </div>
      </header>

      <section
        ref={panelRef}
        className={`auth-panel ${panelOpen ? "open" : ""} auth-panel-${mode}`}
        aria-hidden={!panelOpen}
      >
        <div className="auth-container auth-panel-shell">
          <button type="button" className="auth-close" onClick={closePanel} aria-label={t("landing.auth.close")}>
            x
          </button>
          <div className="auth-panel-copy">
            <span className="auth-tag">{t("landing.auth.tag")}</span>
            <h2>
              {mode === "register"
                ? t("landing.auth.copy.registerTitle")
                : mode === "forgot"
                  ? t("landing.auth.copy.forgotTitle")
                  : t("landing.auth.copy.loginTitle")}
            </h2>
            <p>
              {mode === "register"
                ? t("landing.auth.copy.registerText")
                : mode === "forgot"
                  ? t("landing.auth.copy.forgotText")
                  : t("landing.auth.copy.loginText")}
            </p>
          </div>
          <div className="auth-panel-card">
            <div className="auth-tabs">
              <button
                type="button"
                className={`auth-tab ${mode === "login" || mode === "forgot" ? "active" : ""}`}
                onClick={() => openPanel("login")}
              >
                {t("landing.actions.login")}
              </button>
              <button
                type="button"
                className={`auth-tab ${mode === "register" ? "active" : ""}`}
                onClick={() => openPanel("register")}
              >
                {t("landing.actions.register")}
              </button>
            </div>

            {mode === "login" && (
              <form className="auth-form" onSubmit={twoFactorToken ? handleTwoFactorLogin : handleLogin}>
                {twoFactorToken ? (
                  <>
                    <div className="auth-message success">{t("landing.auth.twoFactor.help")}</div>
                    <div className="auth-field">
                      <label htmlFor="two-factor-code">{t("landing.auth.twoFactor.label")}</label>
                      <input
                        id="two-factor-code"
                        type="text"
                        value={twoFactorCode}
                        onChange={(event) => setTwoFactorCode(event.target.value)}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={8}
                        required
                      />
                    </div>
                    <button type="submit" className="auth-btn auth-btn-primary auth-btn-lg">
                      {t("landing.actions.login")}
                    </button>
                    <button
                      type="button"
                      className="auth-btn auth-btn-secondary auth-btn-lg"
                      onClick={() => {
                        setTwoFactorToken("");
                        setTwoFactorCode("");
                        setLoginPassword("");
                      }}
                    >
                      {t("landing.auth.twoFactor.back")}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="auth-field">
                      <label htmlFor="login-email">{t("landing.auth.fields.email")}</label>
                      <input
                        id="login-email"
                        type="email"
                        value={loginEmail}
                        onChange={(event) => setLoginEmail(event.target.value)}
                        autoComplete="username"
                        autoCapitalize="none"
                        required
                      />
                    </div>
                    <div className="auth-field">
                      <label htmlFor="login-password">{t("landing.auth.fields.password")}</label>
                      <input
                        id="login-password"
                        type="password"
                        value={loginPassword}
                        onChange={(event) => setLoginPassword(event.target.value)}
                        autoComplete="current-password"
                        required
                      />
                    </div>
                    <button
                      type="button"
                      className="auth-link"
                      onClick={() => {
                        setForgotEmail(loginEmail);
                        openPanel("forgot");
                      }}
                    >
                      {t("landing.auth.forgot.link")}
                    </button>
                    <button type="submit" className="auth-btn auth-btn-primary auth-btn-lg">
                      {t("landing.actions.login")}
                    </button>
                  </>
                )}
                {loginNotice && <div className="auth-message success">{loginNotice}</div>}
                {loginError && <div className="auth-message error">{loginError}</div>}
              </form>
            )}

            {mode === "register" && (
              <form className="auth-form auth-form-register" onSubmit={handleRegister} onChange={() => {
                if (!registrationStartedRef.current) {
                  registrationStartedRef.current = trackCampaignEvent('registration_start', {}, i18n.resolvedLanguage || i18n.language);
                }
              }}>
                <div className="auth-form-note">{t("landing.hero.proofs.afterTrial")}</div>
                <div className="auth-grid">
                  <div className="auth-field">
                    <label htmlFor="register-email">{t("landing.auth.fields.businessEmail")}</label>
                    <input
                      id="register-email"
                      type="email"
                      value={registerForm.email}
                      onChange={(event) => handleRegisterChange("email", event.target.value)}
                      autoComplete="email"
                      autoCapitalize="none"
                      required
                    />
                  </div>
                  <div className="auth-field">
                    <label htmlFor="register-role">{t("landing.auth.fields.role")}</label>
                    <select
                      id="register-role"
                      value={registerForm.role}
                      onChange={(event) => handleRegisterChange("role", event.target.value)}
                      required
                    >
                      <option value="">{t("landing.auth.fields.chooseRole")}</option>
                      {roleOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="auth-grid">
                  <div className="auth-field">
                    <label htmlFor="register-first-name">{t("landing.auth.fields.firstName")}</label>
                    <input
                      id="register-first-name"
                      type="text"
                      value={registerForm.firstName}
                      onChange={(event) => handleRegisterChange("firstName", event.target.value)}
                      autoComplete="given-name"
                      required
                    />
                  </div>
                  <div className="auth-field">
                    <label htmlFor="register-last-name">{t("landing.auth.fields.lastName")}</label>
                    <input
                      id="register-last-name"
                      type="text"
                      value={registerForm.lastName}
                      onChange={(event) => handleRegisterChange("lastName", event.target.value)}
                      autoComplete="family-name"
                      required
                    />
                  </div>
                </div>
                <div className="auth-grid">
                  <div className="auth-field">
                    <label htmlFor="register-company">{t("landing.auth.fields.companyName")}</label>
                    <input
                      id="register-company"
                      type="text"
                      value={registerForm.companyName}
                      onChange={(event) => handleRegisterChange("companyName", event.target.value)}
                      autoComplete="organization"
                      required
                    />
                  </div>
                  <div className="auth-field">
                    <label htmlFor="register-password">{t("landing.auth.fields.password")}</label>
                    <input
                      id="register-password"
                      type="password"
                      value={registerForm.password}
                      onChange={(event) => handleRegisterChange("password", event.target.value)}
                      autoComplete="new-password"
                      required
                    />
                  </div>
                </div>
                <label className="auth-check">
                  <input
                    type="checkbox"
                    checked={registerLegalAccepted}
                    onChange={(event) => setRegisterLegalAccepted(event.target.checked)}
                    required
                  />
                  <span>
                    <Trans
                      i18nKey="landing.auth.legalAccept"
                      components={{
                        privacy: <Link to="/privacy" target="_blank" />,
                        terms: <Link to="/voorwaarden" target="_blank" />,
                      }}
                    />
                  </span>
                </label>
                <button type="submit" className="auth-btn auth-btn-primary auth-btn-lg auth-register-submit">
                  {t("landing.hero.proofs.cta")}
                </button>
                {registerNotice && <div className="auth-message success">{registerNotice}</div>}
                {registerError && <div className="auth-message error">{registerError}</div>}
              </form>
            )}

            {mode === "forgot" && (
              <form className="auth-form" onSubmit={handleForgotPassword}>
                <div className="auth-message success">{t("landing.auth.forgot.help")}</div>
                <div className="auth-field">
                  <label htmlFor="forgot-email">{t("landing.auth.fields.email")}</label>
                  <input
                    id="forgot-email"
                    type="email"
                    value={forgotEmail}
                    onChange={(event) => setForgotEmail(event.target.value)}
                    autoComplete="email"
                    autoCapitalize="none"
                    required
                  />
                </div>
                <button type="submit" className="auth-btn auth-btn-primary auth-btn-lg">
                  {t("landing.auth.forgot.submit")}
                </button>
                <button type="button" className="auth-btn auth-btn-secondary auth-btn-lg" onClick={() => openPanel("login")}>
                  {t("landing.auth.forgot.back")}
                </button>
                {forgotNotice && <div className="auth-message success">{forgotNotice}</div>}
                {forgotDevResetUrl && (
                  <a className="auth-btn auth-btn-secondary auth-btn-lg" href={forgotDevResetUrl}>
                    {t("landing.auth.forgot.openLocal")}
                  </a>
                )}
                {forgotError && <div className="auth-message error">{forgotError}</div>}
              </form>
            )}
          </div>
        </div>
      </section>

      <main>
        <section className="auth-hero">
          <div className="auth-container auth-hero-inner">
            <div className="auth-hero-content">
              <div className="auth-eyebrow">{t("landing.hero.eyebrow")}</div>
              <h1>{t("landing.hero.title")}</h1>
              <div className="auth-hero-copy">
                <p>
                  <Trans
                    i18nKey="landing.hero.line1"
                    components={{ underline: <span className="auth-underlined-word" /> }}
                  />
                </p>
                <p>{t("landing.hero.line2")}</p>
                <p>{t("landing.hero.line3")}</p>
              </div>
              <div className="auth-hero-cta">
                <button type="button" className="auth-btn auth-btn-primary auth-btn-lg" onClick={() => openPanel("register")}>
                  {t("landing.hero.proofs.cta")}
                </button>
                <a className="auth-btn auth-btn-secondary auth-btn-lg" href="#product">
                  {t("landing.actions.viewProduct")}
                </a>
              </div>
              <section className="auth-trial-promise" aria-labelledby="trial-promise-title">
                <h2 id="trial-promise-title">{t("landing.hero.proofs.trial")}</h2>
                <ul>
                  {["noCard", "noCharges", "cancel"].map(key => (
                    <li key={key}><CheckCircleOutlineRoundedIcon aria-hidden="true" /><span>{t(`landing.hero.proofs.${key}`)}</span></li>
                  ))}
                </ul>
                <p>{t("landing.hero.proofs.afterTrial")}</p>
              </section>
            </div>
          </div>
        </section>

        <section id="voor-wie" className="auth-section auth-section-map">
          <div className="auth-container">
            <div className="auth-section-head">
              <span className="auth-tag">{t("landing.roles.tag")}</span>
              <h2>{t("landing.roles.title")}</h2>
              <p>{t("landing.roles.intro")}</p>
            </div>

            <div className="auth-role-deep">
              <article className="auth-role-panel retailer">
                <div>
                  <div className="auth-role-kicker">{t("landing.roles.retailer.kicker")}</div>
                  <h3>{t("landing.roles.retailer.title")}</h3>
                  <p>{t("landing.roles.retailer.text")}</p>
                  <div className="auth-role-points">
                    {[1, 2, 3].map((item) => (
                      <div className="auth-role-point" key={`retailer-${item}`}>
                        <b>{item}</b>
                        <div>
                          <strong>{t(`landing.roles.retailer.points.${item}.title`)}</strong>
                          <span>
                            <Trans
                              i18nKey={`landing.roles.retailer.points.${item}.text`}
                              components={{ underline: <span className="auth-underlined-word" /> }}
                            />
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="auth-phone-showcase">
                  <div className="auth-phone-stack" aria-label={t("landing.images.interestStackAria")}>
                    <div className="auth-phone-frame secondary">
                      <LandingPhoneMockup
                        language={currentLanguage}
                        mapImage={landingAssets.heroMap}
                        variant="withdrawn"
                        ariaLabel={t("landing.images.interestSavedAlt")}
                      />
                    </div>
                    <div className="auth-phone-frame">
                      <LandingPhoneMockup
                        language={currentLanguage}
                        mapImage={landingAssets.heroMap}
                        variant="interest"
                        ariaLabel={t("landing.images.interestAlt")}
                      />
                    </div>
                  </div>
                  <div className="auth-phone-caption">{t("landing.images.interestCaption")}</div>
                </div>
              </article>

              <article className="auth-role-panel owner">
                <div>
                  <div className="auth-role-kicker owner">{t("landing.roles.owner.kicker")}</div>
                  <h3>{t("landing.roles.owner.title")}</h3>
                  <p>{t("landing.roles.owner.text")}</p>
                  <div className="auth-role-points">
                    {[1, 2, 3].map((item) => (
                      <div className="auth-role-point" key={`owner-${item}`}>
                        <b>{item}</b>
                        <div>
                          <strong>{t(`landing.roles.owner.points.${item}.title`)}</strong>
                          <span>
                            <Trans
                              i18nKey={`landing.roles.owner.points.${item}.text`}
                              components={{ underline: <span className="auth-underlined-word" /> }}
                            />
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="auth-phone-showcase">
                  <div className="auth-tablet-frame">
                    <img src={landingAssets.ipadOwnerInterests} alt={t("landing.images.ownerAlt")} />
                  </div>
                  <div className="auth-tablet-caption">{t("landing.images.ownerCaption")}</div>
                </div>
              </article>

              <article className="auth-role-panel broker">
                <div>
                  <div className="auth-role-kicker broker">{t("landing.roles.broker.kicker")}</div>
                  <h3>{t("landing.roles.broker.title")}</h3>
                  <p>{t("landing.roles.broker.text")}</p>
                  <div className="auth-role-points">
                    {[1, 2, 3, 4].map((item) => (
                      <div className="auth-role-point" key={`broker-${item}`}>
                        <b>{item}</b>
                        <div>
                          <strong>{t(`landing.roles.broker.points.${item}.title`)}</strong>
                          <span>{t(`landing.roles.broker.points.${item}.text`)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="auth-phone-showcase">
                  <div className="auth-laptop-frame">
                    <img src={landingAssets.laptopBrokerOverview} alt={t("landing.images.brokerAlt")} />
                  </div>
                  <div className="auth-laptop-caption">{t("landing.images.brokerCaption")}</div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section id="hoe-het-werkt" className="auth-section">
          <div className="auth-container auth-split">
            <div>
              <span className="auth-tag">{t("landing.how.tag")}</span>
              <h2>{t("landing.how.title")}</h2>
              <div className="auth-steps">
                {[1, 2, 3].map((item) => (
                  <div className="auth-step" key={`how-${item}`}>
                    <div className="auth-step-num">{item}</div>
                    <div>
                      <h3>{t(`landing.how.steps.${item}.title`)}</h3>
                      <p>{t(`landing.how.steps.${item}.text`)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="auth-angled-phone-scene">
              <div className="auth-angled-phone">
                <LandingPhoneMockup
                  language={currentLanguage}
                  mapImage={landingAssets.heroMap}
                  variant="rental"
                  ariaLabel={t("landing.images.rentalAlt")}
                />
              </div>
            </div>
          </div>
        </section>

        <section id="product" className="auth-section auth-section-dark">
          <div className="auth-container">
            <div className="auth-section-head">
              <span className="auth-tag">{t("landing.product.tag")}</span>
              <h2>{t("landing.product.title")}</h2>
            </div>
            <div className="auth-feature-grid">
              {[1, 2, 3, 4].map((item) => (
                <div className="auth-feature" key={`feature-${item}`}>
                  <h3>{t(`landing.product.features.${item}.title`)}</h3>
                  <p>{t(`landing.product.features.${item}.text`)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="auth-section auth-section-soft">
          <div className="auth-container">
            <span className="auth-tag">{t("landing.visual.tag")}</span>
            <div className="auth-screens-strip">
              <div className="auth-shot-card">
                <div className="auth-street-laptop">
                  <img src={landingAssets.streetView} alt={t("landing.images.streetViewAlt")} />
                </div>
                <div className="auth-shot-caption">
                  <b>{t("landing.visual.title")}</b>
                  <span>{t("landing.visual.text")}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="prijs" className="auth-section">
          <div className="auth-container auth-price-wrap">
            <div>
              <span className="auth-tag">{t("landing.price.tag")}</span>
              <h2>{t("landing.price.title")}</h2>
              <p className="auth-lead">{t("landing.price.text")}</p>
            </div>
            <div className="auth-price-card">
              <h3>{t("landing.price.cardTitle")}</h3>
              <div className="auth-price">
                {t("landing.price.amount")} <small>{t("landing.price.period")}</small>
              </div>
              <p className="auth-lead">{t("landing.price.cardText")}</p>
              <ul>
                {[1, 2, 3, 4].map((item) => (
                  <li key={`price-${item}`}>{t(`landing.price.items.${item}`)}</li>
                ))}
              </ul>
            <button type="button" className="auth-btn auth-btn-primary auth-btn-lg" onClick={() => openPanel("register")}>
                {t("landing.actions.startTrial")}
              </button>
            </div>
          </div>
        </section>

        <section className="auth-final-cta">
          <div className="auth-container">
            <h2>{t("landing.final.title")}</h2>
            <p>{t("landing.final.text")}</p>
            <button type="button" className="auth-btn auth-btn-primary auth-btn-lg" onClick={() => openPanel("register")}>
              {t("landing.actions.startFree")}
            </button>
          </div>
        </section>
      </main>

      <footer className="auth-footer">
        <div className="auth-container auth-footer-inner">
          <div>
            <strong>
              <span className="auth-brand-main">Retailmaps</span>
              <span className="auth-brand-domain">.com</span>
            </strong>
            <div>{t("landing.footer.copyright")}</div>
          </div>
          <div className="auth-footer-links">
            <Link to="/data-veiligheid">
              {t("landing.footer.security", { defaultValue: "Data & security" })}
            </Link>
            <Link to="/privacy">{t("landing.footer.privacy")}</Link>
            <Link to="/voorwaarden">{t("landing.footer.terms")}</Link>
            <a href="mailto:info@retailmaps.com">{t("landing.footer.contact")}</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AuthLandingPage;
